import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS, useUpdateCompanyMutation} from "@/api/companyApi";
import {USER_KEYS} from "@/api/userApi";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {i18n} from "@/i18n";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import {useSettingsCompanyStore} from "@/store/company";
import {useMutation} from "@tanstack/react-query";
import {toast} from "react-toastify";

const useCompanyProfileSave = ({
  agencyBexioContactId,
  companyId,
  subbrandBexioContactId,
}: {
  agencyBexioContactId?: number | null;
  companyId: number;
  subbrandBexioContactId?: number | null;
}) => {
  const isAdmin = useIsAdmin();
  const company = useSettingsCompanyStore(state => state.company);
  const isSubbrand = useSettingsCompanyStore(state => state.company.company_access_level === "SUB_BRAND");
  const savedRanking = useSettingsCompanyStore(state => state.savedCompany.premium_ranking);
  const setError = useSettingsCompanyStore(state => state.setError);
  const validateCompany = useSettingsCompanyStore(state => state.companyFormValidation);

  const {mutateAsync: updateCompany, isPending: isCompanyUpdatePending} = useUpdateCompanyMutation({companyId});
  const {mutateAsync: updateAdminCompany, isPending: isAdminCompanyUpdatePending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompany(companyId),
    mutationFn: () =>
      client.patch(COMPANY_KEYS.mCompany(companyId)[0], {
        disable_chat_close: company.disable_chat_close,
        event_creator: company.event_creator,
        skip_character_limits: company.skip_character_limits,
      }),
  });
  const {mutateAsync: updateAgencyBexioContactId, isPending: isAgencyBexioContactIdUpdatePending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: AGENCY_KEYS.mAgency(company.agency?.id!),
    mutationFn: () =>
      client.patch(AGENCY_KEYS.mAgency(company.agency?.id)[0], {bexio_contact_id: agencyBexioContactId}),
  });
  const {mutateAsync: updateRanking, isPending: isRankingUpdatePending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompanyRanking(companyId),
    mutationFn: () =>
      client.post(COMPANY_KEYS.mCompanyRanking(companyId)[0], {premium_ranking: company.premium_ranking}),
  });
  const {mutateAsync: updateSubbrandBexioContactId, isPending: isSubbrandBexioContactIdUpdatePending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompany(companyId),
    mutationFn: () => client.patch(COMPANY_KEYS.mCompany(companyId)[0], {bexio_contact_id: subbrandBexioContactId}),
  });
  const {mutateAsync: validateInstagramUsername, isPending: isInstagramValidationPending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserInstagramUsernameValidation,
    mutationFn: () =>
      client.post(USER_KEYS.mUserInstagramUsernameValidation[0], {
        check_for_brands: true,
        username: company.username!.replace("@", "").toLowerCase(),
      }),
  });
  const {mutateAsync: validateTikTokUsername, isPending: isTikTokValidationPending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: USER_KEYS.mUserTikTokUsernameValidation,
    mutationFn: () =>
      client.post(USER_KEYS.mUserTikTokUsernameValidation[0], {
        check_for_brands: true,
        username: company.tiktok_username?.replace("@", "").toLowerCase(),
      }),
  });

  const isSaving =
    isAdminCompanyUpdatePending ||
    isAgencyBexioContactIdUpdatePending ||
    isCompanyUpdatePending ||
    isInstagramValidationPending ||
    isRankingUpdatePending ||
    isSubbrandBexioContactIdUpdatePending ||
    isTikTokValidationPending;

  const save = async () => {
    if (!validateCompany() && !isAdmin) return;

    const ranking = company.premium_ranking;
    const rankingChanged = isAdmin && ranking !== savedRanking;
    if (rankingChanged && ranking != null) {
      if (!Number.isInteger(ranking)) {
        setError("premium_ranking", "errors:premium_ranking_invalid");
        return;
      }
      if (ranking < 0 || ranking > 100) {
        setError("premium_ranking", "errors:premium_ranking_out_of_range");
        return;
      }
    }

    if (!isAdmin && company.username !== company.readonlyUsername) {
      await validateInstagramUsername();
    }

    if (!isAdmin && company.tiktok_username?.length && company.tiktok_username !== company.readonlyTikTokUsername) {
      await validateTikTokUsername();
    }

    await updateCompany({
      address_city: company.address_city || null,
      address_postal: company.address_postal || null,
      address_street: company.address_street || null,
      bio: company.bio,
      company_name: company.company_name || null,
      company_overview: company.company_overview,
      country: company.country || null,
      customer_pain_points: company.customer_pain_points,
      interests: company.interests,
      phonenumber: company.phonenumber || null,
      place_description: company.place_description,
      place_id: company.place_id,
      products_overview: company.products_overview,
      tiktok_username: company.tiktok_username,
      username: company.username,
      website: company.website || null,
    });

    if (isAdmin) {
      await updateAdminCompany();

      if (rankingChanged) {
        await updateRanking();
      }

      if (agencyBexioContactId !== undefined && company.agency?.id && !isSubbrand) {
        await updateAgencyBexioContactId();
      }

      if (subbrandBexioContactId !== undefined && isSubbrand) {
        await updateSubbrandBexioContactId();
      }
    }

    toast(i18n.t("toast_global_success"), {type: "success"});

    capturePostHogEvent(PostHogEvents.CompanyProfileUpdated, {companyId});

    queryClient.invalidateQueries({
      queryKey: COMPANY_KEYS.qCompany(companyId),
    });

    if (company.agency?.id) {
      queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencySubbrands(company.agency.id)});
    }
  };

  return {isSaving, save};
};

export default useCompanyProfileSave;
